import { CircleCheck, CircleDashed, CircleDot, CirclePause, CircleStop, Clock, Laptop, LoaderCircle, Monitor, Server } from '@orgus/ui/icons';
import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { OS_LABEL, machineLiveness } from '@orgus/protocol';
import type { CloudVm, Job, Machine, MachineDrain, MachineLoadEntry, MachineLoadState, MachineOs, Session, WorkBudgetView } from '@orgus/protocol';
import {
  LiveNumber,
  Pressable,
  REVEAL_GROUP,
  RevealSlot,
  StateBar,
  Tooltip,
  TrendArea,
  cn,
  formatBytes,
  formatDuration,
  formatInt,
  formatPercent,
  machineLabel,
  plural,
} from '@orgus/ui';
import type { DataSlot, RowStatusProps } from '@orgus/ui';
import type { MachineSample } from '../../lib/store.ts';
import { useExt, useOrgus } from '../../lib/store.ts';
import { MACHINE_LOAD_STATE_EXT, entryFor } from '../machine-load/model.ts';
import { formatHolderName, formatHoldersSummary } from '../machine-load/cpuWaiting.ts';
import { disabledText, machineState, useMachineDrain, workingCount } from './controls.ts';
import type { MachineTone } from './controls.ts';
import { MachineMenu } from './MachineMenu.tsx';
import type { MenuFailure } from './MachineMenu.tsx';

/** The card surface the console uses for panels and tiles (Overview's look). */
export const CARD_SURFACE = 'rounded-card border border-border bg-card surface-sheen shadow-elevation-flat';

/** CPU and memory keep their chart slots on every figure of the page (data-viz.md: a series keeps its slot). */
export const CPU_SLOT: DataSlot = 1;
export const MEM_SLOT: DataSlot = 4;

export interface MachineCardProps {
  machine: Machine;
  history: MachineSample[] | undefined;
  sessions: Session[];
  jobs: Job[];
  now: number;
  /** The newest agent version across machines, to flag older agents. */
  latestVersion: string;
  onOpen: () => void;
  onRevoke: () => void;
  /** The one chip beside the name (fleet: below minimum, update available or in progress, test ring). */
  badges?: ReactNode;
  /** The release "Update agent" offers (fleet), or null. */
  updateTo?: string | null;
  onUpdate?: () => Promise<void>;
  /** A menu action that failed, for the page's banner. */
  onFail: (f: MenuFailure) => void;
  selected?: boolean;
  /** The VM Orgus created for this machine (ADR 0106 decision 3): its status and menu items. */
  vm?: CloudVm | null;
  /** One caption line under the metrics: how a VM ends and what it costs now (ADR 0106). */
  note?: ReactNode;
}

/** The machine's one status, icon plus label: online, not reporting (attention) or offline. */
export function machineRowStatus(m: Machine, now: number): Pick<RowStatusProps, 'tone' | 'icon' | 'label'> {
  const live = machineLiveness(m, now);
  if (live === 'online') return { tone: 'neutral', icon: 'done', label: 'Online' };
  if (live === 'stale') return { tone: 'attention', icon: 'warning', label: `Not reporting for ${formatDuration(now - m.lastSeen)}` };
  return { tone: 'neutral', icon: 'offline', label: `Offline for ${formatDuration(now - m.lastSeen)}` };
}

/** Live sessions in words, so a count never stands alone: "2 working, 1 needs you, 1 idle". */
export function sessionCountsText(sessions: Pick<Session, 'status'>[]): string {
  const n = (s: Session['status']) => sessions.filter((x) => x.status === s).length;
  const parts = [
    n('waiting') && `${formatInt(n('waiting'))} needs you`,
    n('working') && `${formatInt(n('working'))} working`,
    n('idle') && `${formatInt(n('idle'))} idle`,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : 'No live sessions';
}

/** The counts in words; only "needs you" carries colour. */
export function SessionCounts({ sessions, className }: { sessions: Pick<Session, 'status'>[]; className?: string }) {
  const waiting = sessions.filter((x) => x.status === 'waiting').length;
  const text = sessionCountsText(sessions);
  if (!waiting) return <span className={cn('min-w-0 truncate num', sessions.length ? 'text-fg-muted' : 'text-fg-subtle', className)}>{text}</span>;
  const head = `${formatInt(waiting)} needs you`;
  const rest = text.slice(head.length);
  return (
    <span className={cn('min-w-0 truncate text-fg-muted num', className)}>
      <span className="font-medium text-status-attention">{head}</span>
      {rest}
    </span>
  );
}

/** Live sessions as work states, for a StateBar. */
export function sessionStates(sessions: Pick<Session, 'status'>[]) {
  const n = (s: Session['status']) => sessions.filter((x) => x.status === s).length;
  return { 'needs-you': n('waiting'), running: n('working'), stale: n('idle') };
}

/** The machine's system in words, the same everywhere (card, hover card): "macOS, arm64, 12 CPUs". */
export function machineSystem(m: Pick<Machine, 'os' | 'arch' | 'cpus'>): string {
  return [OS_LABEL[m.os], m.arch, `${formatInt(m.cpus)} CPUs`].filter(Boolean).join(', ');
}

/** The machine's facts once: OS, architecture, CPUs, and the hostname only when it is not the name. */
export function machineFacts(m: Machine): string {
  const host = m.hostname && m.hostname.toLowerCase() !== m.name.toLowerCase() && !m.hostname.toLowerCase().startsWith(`${m.name.toLowerCase()}.`) ? m.hostname : null;
  return [machineSystem(m), host].filter(Boolean).join(', ');
}

const OS_ICON: Record<MachineOs, ReactNode> = { macos: <Laptop />, windows: <Monitor />, linux: <Server /> };

/** The machine's tile: its kind of computer in a neutral rounded square, like a project's initial. */
export function OsTile({ os, quiet, className }: { os: MachineOs; quiet?: boolean; className?: string }) {
  return (
    <span aria-hidden className={cn('flex size-8 shrink-0 items-center justify-center rounded-control bg-active shadow-elevation-highlight [&_svg]:size-4', quiet ? 'text-fg-faint' : 'text-fg-muted', className)}>
      {OS_ICON[os]}
    </span>
  );
}

const TONE_TEXT: Record<MachineTone, string> = {
  success: 'text-status-success',
  working: 'text-status-working',
  attention: 'font-medium text-status-attention',
  neutral: 'text-fg-subtle',
};

/**
 * The machine's one status, icon plus label. On the Machines page and its drawer (`coloured`,
 * ADR 0106 decision 1): Online green, Working cyan and spinning, Not reporting amber, Disabled
 * and Offline neutral. Elsewhere it stays neutral (status.md rule 1).
 */
export function MachineStatus({ machine: m, now, className, coloured, working = 0, drain = null, vm = null }: { machine: Machine; now: number; className?: string; coloured?: boolean; working?: number; drain?: MachineDrain | null; vm?: CloudVm | null }) {
  if (!coloured) {
    const live = machineLiveness(m, now);
    const Icon = live === 'online' ? CircleDot : live === 'stale' ? Clock : CircleDashed;
    const text = live === 'online' ? 'Online' : `${machineLabel(m, now)} ${formatDuration(now - m.lastSeen)}`;
    return (
      <span className={cn('inline-flex min-w-0 items-center gap-2 text-ui', live === 'stale' ? 'font-medium text-status-attention' : 'text-fg-subtle', className)}>
        <Icon className="size-4 shrink-0" aria-hidden />
        <span className="truncate">{text}</span>
      </span>
    );
  }
  const st = machineState(m, now, { working, drain, vm });
  const icon =
    st.key === 'online' ? <CircleCheck className="size-4 shrink-0" aria-hidden />
    : st.key === 'working' || st.key === 'starting' || st.key === 'stopping' || st.key === 'deleting' ? <LoaderCircle className="size-4 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden />
    : st.key === 'stale' ? <Clock className="size-4 shrink-0" aria-hidden />
    : st.key === 'disabled' ? <CirclePause className="size-4 shrink-0" aria-hidden />
    : st.key === 'stopped' ? <CircleStop className="size-4 shrink-0" aria-hidden />
    : <CircleDashed className="size-4 shrink-0" aria-hidden />;
  const body = (
    <span data-machine-state={st.key} className={cn('inline-flex min-w-0 items-center gap-2 text-ui', TONE_TEXT[st.tone], className)}>
      {icon}
      <span className="truncate">{st.label}</span>
    </span>
  );
  return st.key === 'disabled' && drain ? (
    <Tooltip content={disabledText(drain, now)}>
      <span tabIndex={0} className="inline-flex min-w-0 rounded-control">{body}</span>
    </Tooltip>
  ) : (
    body
  );
}

function Metric({ label, value, values, slot, hint, dim }: { label: string; value: number; values: number[]; slot: DataSlot; hint?: string; dim: boolean }) {
  const reading = (
    <span className="flex min-w-0 items-baseline justify-between gap-2">
      <span className="text-caption text-fg-subtle">{label}</span>
      {dim ? <span className="text-caption text-fg-subtle">No reading</span> : <LiveNumber value={value} format={(n) => formatPercent(n)} className="text-lead font-semibold text-fg" />}
    </span>
  );
  return (
    <div className="flex min-w-0 flex-col gap-2">
      {hint && !dim ? (
        <Tooltip content={hint}>
          <span tabIndex={-1}>{reading}</span>
        </Tooltip>
      ) : (
        reading
      )}
      {/* No reading: no flat or dashed chart that reads as broken, just the quiet line above and the same height. */}
      {dim ? <div className="h-10" aria-hidden /> : <TrendArea values={values} min={0} max={1} slot={slot} height={40} guides={[0.5]} label={`${label}, recent samples, now ${formatPercent(value)}`} />}
    </div>
  );
}

function SlotBudgetBar({ machineId }: { machineId: string }) {
  const loadSlice = useExt<MachineLoadState>(MACHINE_LOAD_STATE_EXT);
  const entry = entryFor(loadSlice, machineId);
  const work = entry?.work ?? null;
  const sessionsById = useOrgus((s) => s.sessions);
  const projectsById = useOrgus((s) => s.projects);
  const names = useMemo(() => ({ sessions: sessionsById, projects: projectsById }), [sessionsById, projectsById]);

  if (!work || work.budget <= 0) return null;
  const summary = formatHoldersSummary(work, names);
  const waitingCount = work.waiting.length;

  const grouped = new Map<string, number>();
  for (const lease of work.leases) {
    const name = formatHolderName(lease, names);
    grouped.set(name, (grouped.get(name) ?? 0) + lease.slots);
  }
  const segments = [...grouped.entries()].map(([name, slots]) => ({ name, slots, pct: (slots / work.budget) * 100 }));
  const waitingNames = work.waiting.map((w) => formatHolderName(w, names)).join(', ');

  return (
    <div className="pointer-events-auto flex flex-col gap-1 rounded-control bg-hover/50 p-2 text-caption">
      <div className="flex items-center justify-between text-fg-subtle font-medium">
        <span>Slot budget ({work.used}/{work.budget})</span>
        {waitingCount > 0 && <span className="font-medium text-status-attention">{formatInt(waitingCount)} waiting</span>}
      </div>
      <Tooltip content={summary}>
        <div className="flex h-2 w-full overflow-hidden rounded-pill bg-active">
          {segments.map((s, i) => (
            <span
              key={i}
              className="h-full bg-fg-muted border-r border-bg last:border-r-0"
              style={{ width: `${s.pct}%` }}
            />
          ))}
        </div>
      </Tooltip>
      {waitingCount > 0 && (
        <p className="truncate text-fg-subtle" title={`Waiting line: ${waitingNames}`}>
          <span className="font-medium text-fg-muted">Waiting line:</span> {waitingNames}
        </p>
      )}
    </div>
  );
}

/** One machine: its kind, status, live CPU and memory trends, and the work on it. */
export function MachineCard({ machine: m, history, sessions, jobs, now, onOpen, onRevoke, badges, updateTo, onUpdate, onFail, selected, vm = null, note }: MachineCardProps) {
  const drain = useMachineDrain(m.id) ?? null;
  const live = machineLiveness(m, now);
  const dim = live !== 'online';
  const memUsed = m.memTotal > 0 ? 1 - m.memFree / m.memTotal : 0;
  const loads = useMemo(() => (history ?? []).map((s) => s.load), [history]);
  const mems = useMemo(() => (history ?? []).map((s) => s.mem), [history]);
  const jobsRunning = jobs.filter((j) => j.state === 'running').length;
  const facts = [OS_LABEL[m.os], m.arch, `${formatInt(m.cpus)} CPUs`].join(', ');

  return (
    <article
      className={cn(
        REVEAL_GROUP,
        CARD_SURFACE,
        '@container relative flex h-full flex-col gap-4 p-4 transition-colors duration-(--duration-fast) hover:border-border-strong hover:bg-raised',
        selected && 'border-accent',
      )}
      aria-label={`${m.name}, ${machineLabel(m, now)}`}
    >
      {/* The whole card opens the machine; the chip and the menu sit above this layer. */}
      <Pressable onClick={onOpen} aria-label={`Open ${m.name}`} className="absolute inset-0 rounded-card" />

      <header className="pointer-events-none relative flex min-w-0 items-center gap-3">
        <OsTile os={m.os} quiet={dim} />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex min-w-0 items-center gap-3">
            <h3 className={cn('min-w-0 flex-1 truncate text-ui font-medium', dim ? 'text-fg-muted' : 'text-fg')}>{m.name}</h3>
            <MachineStatus machine={m} now={now} className="shrink-0" coloured working={workingCount(sessions, jobs)} drain={drain} vm={vm} />
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <p data-meta="" className="min-w-0 truncate text-caption text-fg-subtle" title={machineFacts(m)}>
              {facts}
              {m.local ? ', runs the hub' : ''}
            </p>
            {/* A narrow card keeps its name and facts; the chip waits for room (its fact is in the drawer too). */}
            <span className="pointer-events-auto relative z-10 ml-auto hidden min-w-0 shrink-0 items-center @xs:flex">{badges}</span>
          </div>
        </div>
        <RevealSlot className="pointer-events-auto relative z-10 -mr-2 shrink-0">
          <MachineMenu machine={m} running={sessions.length} updateTo={updateTo} onUpdate={onUpdate} onRemove={onRevoke} onFail={onFail} vm={vm} />
        </RevealSlot>
      </header>

      <div className="pointer-events-none relative grid grid-cols-2 gap-6">
        <Metric label="CPU" value={m.load} values={loads} slot={CPU_SLOT} dim={dim} />
        <Metric label="Memory" value={memUsed} values={mems} slot={MEM_SLOT} hint={`${formatBytes(m.memTotal - m.memFree)} of ${formatBytes(m.memTotal, 0)}`} dim={dim} />
      </div>

      <SlotBudgetBar machineId={m.id} />

      {note && <p className="pointer-events-none relative min-w-0 truncate text-caption text-fg-subtle num">{note}</p>}

      <footer className="pointer-events-none relative mt-auto flex min-w-0 items-center gap-3 text-ui">
        {sessions.length > 0 && <StateBar counts={sessionStates(sessions)} className="w-12 shrink-0" />}
        <SessionCounts sessions={sessions} />
        {jobsRunning > 0 && <span className="ml-auto shrink-0 text-caption text-fg-subtle num">{plural(jobsRunning, 'command')} running</span>}
      </footer>
    </article>
  );
}
